import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PlayerForm = ({ player, teamId, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: player?.name || '',
    role: player?.role || 'batsman',
    jerseyNumber: player?.jerseyNumber || '',
    battingStyle: player?.battingStyle || 'right-hand',
    bowlingStyle: player?.bowlingStyle || 'none',
    teamId: player?.teamId || teamId
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.name?.trim()) {
      setError('Player name is required');
      return;
    }

    if (!formData?.teamId) {
      setError('Team is required');
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err?.message || 'Failed to save player');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[1200]">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">
              {player ? 'Edit Player' : 'Add New Player'}
            </h2>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              disabled={loading}>
              <Icon name="X" className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
              <Icon name="AlertCircle" className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Player Name *
              </label>
              <Input
                type="text"
                name="name"
                value={formData?.name}
                onChange={handleChange}
                placeholder="Enter player name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Role *
              </label>
              <select
                name="role"
                value={formData?.role || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                required>
                <option value="Batter">Batter</option>
                <option value="Bowler">Bowler</option>
                <option value="All-rounder">All-Rounder</option>
                <option value="Wicket Keeper">Wicket-Keeper</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Jersey Number
              </label>
              <Input
                type="number"
                name="jerseyNumber"
                value={formData?.jerseyNumber}
                onChange={handleChange}
                placeholder="Enter jersey number"
                min="1"
                max="99"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Batting Style
              </label>
              <select
                name="battingStyle"
                value={formData?.battingStyle}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                <option value="right-hand">Right-Hand</option>
                <option value="left-hand">Left-Hand</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Bowling Style
              </label>
              <select
                name="bowlingStyle"
                value={formData?.bowlingStyle}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                <option value="none">None</option>
                <option value="right-arm-fast">Right-Arm Fast</option>
                <option value="left-arm-fast">Left-Arm Fast</option>
                <option value="right-arm-medium">Right-Arm Medium</option>
                <option value="left-arm-medium">Left-Arm Medium</option>
                <option value="right-arm-spin">Right-Arm Spin</option>
                <option value="left-arm-spin">Left-Arm Spin</option>
                <option value="leg-spin">Leg Spin</option>
                <option value="off-spin">Off Spin</option>
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={loading}
                className="flex-1">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                disabled={loading}
                className="flex-1">
                {loading ? 'Saving...' : player ? 'Update Player' : 'Add Player'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PlayerForm;